import { PreviewMessageType } from '@webcontainer/api';
import { lazy } from 'react';
import { Counter } from '~/components/ui/Counter';
import { LazyLoadWrapper, retry } from '~/components/ui/LazyLoadWrapper';
import type { PotentialPreviewProblem } from '~/types/chat';
import { classNames } from '~/utils/classNames';
import styles from './PreviewRenderer.module.scss';

const ConsoleInspector = lazy(retry(() => import('./ConsoleInspector')));

export interface PreviewRendererProps extends PotentialPreviewProblem {
  count: number;
  stack?: string;
}

const TRIM_FIRST_SPACE = /^ /gm;

export function PreviewRenderer({ count, error }: PreviewRendererProps) {
  let stack: string | null = null;
  let message: React.ReactNode = 'message' in error ? error.message : null;

  if ('stack' in error && error.stack) {
    const lines = error.stack.split('\n');

    if (!message) {
      message = lines.shift();
    }

    stack = lines.join('\n');
  }

  if (error.type === PreviewMessageType.ConsoleError) {
    message = (
      <LazyLoadWrapper name="ConsoleInspector">
        <ConsoleInspector {...error} />
      </LazyLoadWrapper>
    );
  }

  return (
    <div className="flex items-center px-3 py-4 gap-3 text-sm rounded-lg bg-bolt-elements-messages-background font-mono leading-4.5">
      <Counter className="mb-auto" overflow={10}>
        {count}
      </Counter>

      {stack ? (
        <details
          className="w-full overflow-hidden"
          onClick={(event) => {
            const target = event.target as HTMLElement;
            const details = event.currentTarget;
            const treeItem = target.closest('li[role=treeitem]');

            if (!treeItem) {
              return;
            }

            const expanded = treeItem?.ariaExpanded;

            // don't toggle main details when toggling other treeItems in ConsoleRenderer
            requestAnimationFrame(() => {
              if (treeItem.ariaExpanded !== expanded) {
                details.open = !details.open;
              }
            });
          }}
        >
          <summary className={classNames(styles.PreviewError, 'cursor-pointer ![&_*]:cursor-pointer')}>
            <div className="ml-2">{message}</div>
          </summary>
          <pre className="overflow-scroll mt-1.5 text-bolt-elements-textSecondary text-[0.6rem] leading-4">
            {stack.replace(TRIM_FIRST_SPACE, '')}
          </pre>
        </details>
      ) : (
        <div className={styles.PreviewError}>{message}</div>
      )}
    </div>
  );
}
